import { createSelector } from '@reduxjs/toolkit';

import type { CaptionLine } from './callCaptionsSlice';

import type { RootState } from 'features/app/store/store';
import { normalizePhoneNumber } from 'features/call/call-base/helpers';
import {
  getCaptionTurnBuildOptions,
  type CaptionDisplayConfig,
} from 'features/call/call-captions/constants';
import {
  buildCaptionSpeakerColorMap,
  buildCaptionTurns,
  resolveCaptionSpeakerColorVariant,
  resolveCaptionSpeakerLabel,
} from 'features/call/call-captions/helpers';
import {
  hearingsSelector,
  primaryHearingSelector,
} from 'features/call/call-hearing/store';

const UNKNOWN_HEARING_NAME_LABELS = new Set([
  'unknown hearing person',
  'unknownhearingname',
]);

export const selectRawCaptionLines = ({
  callCaptions,
}: RootState): CaptionLine[] => callCaptions.lines;

export const getVisibleCaptions = (
  captionLines: CaptionLine[]
): CaptionLine[] => {
  const partialCaptions = captionLines.filter((caption) => caption.isPartial);
  const finalCaptions = captionLines.filter((caption) => !caption.isPartial);

  const getSortKey = (caption: CaptionLine) =>
    caption.startedAtMs ?? caption.receivedAtMs;

  const sortedFinalCaptions = finalCaptions
    .sort((left, right) => getSortKey(left) - getSortKey(right))
    .slice();
  const latestPartialCaption = partialCaptions
    .toSorted((left, right) => getSortKey(left) - getSortKey(right))
    .slice(-1);

  return [...sortedFinalCaptions, ...latestPartialCaption];
};

/** Visible lines after partial dedup — same output WSBridge relied on before raw storage. */
export const selectCallCaptionLines = createSelector(
  [selectRawCaptionLines],
  getVisibleCaptions
);

export const selectCaptionsEnabled = ({ callCaptions }: RootState) =>
  callCaptions.captionsEnabled;

export const selectCaptionTrimOffsets = ({ callCaptions }: RootState) =>
  callCaptions.trimOffsets ?? {};

export const selectCaptionsRegionActive = createSelector(
  [selectCallCaptionLines],
  (lines) => lines.length > 0
);

export const selectShouldShowCaptionsRegion = createSelector(
  [
    selectCallCaptionLines,
    selectCaptionsEnabled,
    (_state: RootState, holdRegionOpen: boolean) => holdRegionOpen,
  ],
  (lines, captionsEnabled, holdRegionOpen) =>
    captionsEnabled && (lines.length > 0 || holdRegionOpen)
);

const selectKnownHearingName = createSelector(
  [primaryHearingSelector],
  (primaryHearing) => {
    const hearingName = primaryHearing?.name?.trim();

    if (!hearingName) {
      return null;
    }

    return UNKNOWN_HEARING_NAME_LABELS.has(hearingName.toLowerCase())
      ? null
      : hearingName;
  }
);

const selectPrimaryHearingPhoneNumber = createSelector(
  [primaryHearingSelector],
  (primaryHearing) => normalizePhoneNumber(primaryHearing?.phoneNumber ?? '')
);

const selectCaptionSpeakerColorMap = createSelector(
  [hearingsSelector],
  (hearings) => buildCaptionSpeakerColorMap(hearings)
);

export const selectResolvedCaptionLines = createSelector(
  [
    selectCallCaptionLines,
    selectKnownHearingName,
    selectPrimaryHearingPhoneNumber,
    selectCaptionSpeakerColorMap,
  ],
  (
    visibleCaptions,
    knownHearingName,
    primaryHearingPhoneNumber,
    speakerColorMap
  ) =>
    visibleCaptions.map((caption) => ({
      ...caption,
      speakerLabel: resolveCaptionSpeakerLabel({
        speakerLabel: caption.speakerLabel,
        speakerRole: caption.speakerRole,
        knownHearingName,
        primaryHearingPhoneNumber,
      }),
      speakerColorVariant: resolveCaptionSpeakerColorVariant({
        speakerRole: caption.speakerRole,
        speakerLabel: caption.speakerLabel,
        speakerColorMap,
      }),
    }))
);

export const makeSelectCaptionTurns = (config: CaptionDisplayConfig) =>
  createSelector(
    [selectResolvedCaptionLines, selectCaptionTrimOffsets],
    (resolvedLines, trimOffsets) => {
      const turns = buildCaptionTurns(
        resolvedLines,
        getCaptionTurnBuildOptions(config)
      );

      return turns.map((turn) => {
        const offset = trimOffsets[turn.key] ?? 0;
        const trimmedText = turn.text.slice(offset).trimStart();

        return {
          ...turn,
          text: trimmedText,
        };
      });
    }
  );
